import type {
  CommandRequest,
  CommandResult,
  EditorSnapshot,
  MaterialData,
  MeshData,
  OperationRecord,
  SceneDocument,
  SelectionState,
  TextureAsset,
  Vec3,
} from "../types";
import {
  bevelEdges,
  componentVertexIds,
  createCubeMesh,
  extrudeBoundaryEdges,
  extrudeFaces,
  insertEdgeLoop,
  projectBoxUV,
  projectPlanarUV,
  refreshEdgeIds,
  transformVertices,
  validateMesh,
} from "./mesh";

export const DEFAULT_MATERIAL: MaterialData = {
  id: "material_default",
  name: "Feral Standard",
  color: "#8fa39a",
  roughness: 0.72,
  metallic: 0.05,
};

function stableId(namespace: string, value: string): string {
  let hashA = 0x811c9dc5;
  let hashB = 0x9e3779b9;
  const input = `${namespace}:${value}`;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    hashA = Math.imul(hashA ^ code, 0x01000193) >>> 0;
    hashB = Math.imul(hashB ^ (code + index), 0x85ebca6b) >>> 0;
  }
  const block = (value: number) => value.toString(16).padStart(8, "0");
  return `${namespace}_${block(hashA)}${block(hashB)}`;
}

export function createInitialScene(): SceneDocument {
  const cube = createCubeMesh("pCube1", 100, [1, 1, 1]);
  cube.id = "object_initial_cube";
  return {
    format: "feral3d",
    formatVersion: 1,
    revision: 1,
    units: "centimeter",
    objects: [cube],
    materials: [structuredClone(DEFAULT_MATERIAL)],
    textures: [],
    operations: [],
    activeObjectId: cube.id,
  };
}

export function createInitialSnapshot(): EditorSnapshot {
  const scene = createInitialScene();
  return {
    scene,
    selection: { mode: "object", objectIds: [scene.activeObjectId!], componentIds: [] },
    tools: { tool: "select", space: "world", display: "shaded", quadView: false, pivotEditing: false },
  };
}

interface AppliedCommand {
  snapshot: EditorSnapshot;
  result: CommandResult;
  record?: OperationRecord;
  label?: string;
}

function success(scene: SceneDocument, objectIds: string[], warnings: string[] = [], lineage: Record<string, number[]> = {}): CommandResult {
  return {
    ok: true,
    revision: scene.revision,
    affectedObjectIds: objectIds,
    affectedComponents: [],
    lineage,
    warnings,
  };
}

function failure(scene: SceneDocument, code: string, message: string): AppliedCommand {
  return {
    snapshot: undefined as never,
    result: {
      ok: false,
      revision: scene.revision,
      affectedObjectIds: [],
      affectedComponents: [],
      lineage: {},
      warnings: [],
      error: { code, message },
    },
  };
}

function activeMesh(snapshot: EditorSnapshot, requestedId?: string): MeshData {
  const objectId = requestedId ?? snapshot.scene.activeObjectId ?? snapshot.selection.objectIds[0];
  const mesh = snapshot.scene.objects.find((object) => object.id === objectId);
  if (!mesh) throw new Error("Select a mesh object first.");
  return mesh;
}

function replaceObject(scene: SceneDocument, mesh: MeshData): void {
  const index = scene.objects.findIndex((object) => object.id === mesh.id);
  if (index < 0) throw new Error("The target mesh no longer exists.");
  scene.objects[index] = mesh;
}

function operationRecord(
  source: SceneDocument,
  type: string,
  label: string,
  objectIds: string[],
  componentIds: number[],
  params: Record<string, unknown>,
  lineage: Record<string, number[]>,
): OperationRecord {
  const sequence = source.operations.length + 1;
  const identity = JSON.stringify({ sequence, type, objectIds, componentIds, params });
  return {
    id: stableId("operation", identity),
    sequence,
    kernelVersion: 1,
    type,
    label,
    inputRevision: source.revision,
    resultRevision: source.revision + 1,
    objectIds,
    targetComponentIds: componentIds,
    params,
    lineage,
    createdAt: new Date(sequence).toISOString(),
  };
}

function finalize(
  base: EditorSnapshot,
  next: EditorSnapshot,
  record: OperationRecord,
  warnings: string[] = [],
  lineage: Record<string, number[]> = {},
): AppliedCommand {
  next.scene.revision = base.scene.revision + 1;
  record.resultRevision = next.scene.revision;
  next.scene.operations.push(record);
  const invariantWarnings = next.scene.objects.flatMap((mesh) => validateMesh(mesh));
  return {
    snapshot: next,
    record,
    label: record.label,
    result: success(next.scene, record.objectIds, [...warnings, ...invariantWarnings], lineage),
  };
}

export function applyCommand(baseSnapshot: EditorSnapshot, request: CommandRequest): AppliedCommand {
  if (request.expectedRevision !== undefined && request.expectedRevision !== baseSnapshot.scene.revision) {
    const failed = failure(baseSnapshot.scene, "REVISION_CONFLICT", `Expected scene revision ${request.expectedRevision}, but the editor is at revision ${baseSnapshot.scene.revision}. Refresh the scene summary and retry.`);
    failed.snapshot = baseSnapshot;
    return failed;
  }
  const next = structuredClone(baseSnapshot);
  const args = request.args as Record<string, unknown>;
  try {
    switch (request.type) {
      case "selection.set": {
        const selection = structuredClone((args.selection ?? args) as SelectionState);
        selection.objectIds = selection.objectIds.filter((id) => next.scene.objects.some((object) => object.id === id));
        if (selection.mode === "object") selection.componentIds = [];
        next.selection = selection;
        next.scene.activeObjectId = selection.objectIds.at(-1) ?? next.scene.activeObjectId;
        return { snapshot: next, label: "Set Selection", result: success(next.scene, selection.objectIds) };
      }
      case "tool.set": {
        next.tools = { ...next.tools, ...(args.tools as Partial<EditorSnapshot["tools"]> ?? {}) };
        return { snapshot: next, label: "Set Tool", result: success(next.scene, next.selection.objectIds) };
      }
      case "scene.createCube": {
        const size = Number(args.size ?? 100);
        const divisions = (args.divisions ?? [args.divisionsX ?? 1, args.divisionsY ?? 1, args.divisionsZ ?? 1]) as Vec3;
        const count = next.scene.objects.filter((object) => object.name.startsWith("pCube")).length + 1;
        const mesh = createCubeMesh(String(args.name ?? `pCube${count}`), size, divisions);
        mesh.id = stableId("object", request.id);
        next.scene.objects.push(mesh);
        next.scene.activeObjectId = mesh.id;
        next.selection = { mode: "object", objectIds: [mesh.id], componentIds: [] };
        const record = operationRecord(baseSnapshot.scene, request.type, "Create Cube", [mesh.id], [], { size, divisions }, {});
        return finalize(baseSnapshot, next, record);
      }
      case "scene.deleteObjects": {
        const objectIds = (args.objectIds ?? next.selection.objectIds) as string[];
        if (!objectIds.length) throw new Error("Select one or more objects to delete.");
        next.scene.objects = next.scene.objects.filter((object) => !objectIds.includes(object.id));
        next.scene.activeObjectId = next.scene.objects[0]?.id;
        next.selection = { mode: "object", objectIds: next.scene.activeObjectId ? [next.scene.activeObjectId] : [], componentIds: [] };
        const record = operationRecord(baseSnapshot.scene, request.type, "Delete Objects", objectIds, [], {}, {});
        return finalize(baseSnapshot, next, record);
      }
      case "scene.duplicateObject": {
        const source = activeMesh(next, args.objectId as string | undefined);
        const copy = structuredClone(source);
        copy.id = stableId("object", request.id);
        copy.name = `${source.name}_copy`;
        copy.transform.position = [source.transform.position[0] + 20, source.transform.position[1], source.transform.position[2]];
        next.scene.objects.push(copy);
        next.scene.activeObjectId = copy.id;
        next.selection = { mode: "object", objectIds: [copy.id], componentIds: [] };
        const record = operationRecord(baseSnapshot.scene, request.type, "Duplicate Object", [copy.id], [], { sourceObjectId: source.id }, {});
        return finalize(baseSnapshot, next, record);
      }
      case "scene.renameObject": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const name = String(args.name ?? "Mesh").trim();
        if (!name) throw new Error("Object names cannot be empty.");
        mesh.name = name;
        const record = operationRecord(baseSnapshot.scene, request.type, "Rename Object", [mesh.id], [], { name }, {});
        return finalize(baseSnapshot, next, record);
      }
      case "object.setTransform": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        if (args.position) mesh.transform.position = args.position as Vec3;
        if (args.rotation) mesh.transform.rotation = args.rotation as Vec3;
        if (args.scale) mesh.transform.scale = args.scale as Vec3;
        if (args.pivot) mesh.transform.pivot = args.pivot as Vec3;
        const record = operationRecord(baseSnapshot.scene, request.type, "Transform Object", [mesh.id], [], structuredClone(args), {});
        return finalize(baseSnapshot, next, record);
      }
      case "component.transform": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const mode = (args.mode ?? next.selection.mode) as "vertex" | "edge" | "face";
        const componentIds = (args.componentIds ?? next.selection.componentIds) as number[];
        const vertices = componentVertexIds(mesh, mode, componentIds);
        if (!vertices.length) throw new Error("Select vertices, edges, or faces to transform.");
        replaceObject(next.scene, transformVertices(mesh, vertices, args.matrix as number[]));
        const record = operationRecord(baseSnapshot.scene, request.type, "Transform Components", [mesh.id], componentIds, { mode, matrix: args.matrix }, {});
        return finalize(baseSnapshot, next, record);
      }
      case "mesh.extrude": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const mode = (args.mode ?? next.selection.mode) as "face" | "edge";
        const componentIds = (args.componentIds ?? next.selection.componentIds) as number[];
        const distance = Number(args.distance ?? 20);
        const divisions = Number(args.divisions ?? 1);
        const offset = Number(args.offset ?? 0);
        const keepTogether = Boolean(args.keepTogether ?? true);
        const outcome = mode === "face"
          ? extrudeFaces(mesh, componentIds, distance, divisions, offset, keepTogether)
          : extrudeBoundaryEdges(mesh, componentIds, distance, divisions);
        replaceObject(next.scene, outcome.mesh);
        next.selection.componentIds = outcome.selectedIds;
        next.selection.mode = mode === "edge" ? "face" : mode;
        const params = { mode, distance, divisions, offset, keepTogether };
        const record = operationRecord(baseSnapshot.scene, request.type, mode === "face" ? "Extrude Faces" : "Extrude Edges", [mesh.id], componentIds, params, outcome.lineage);
        return finalize(baseSnapshot, next, record, outcome.warnings, outcome.lineage);
      }
      case "mesh.bevel": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const componentIds = (args.componentIds ?? next.selection.componentIds) as number[];
        const width = Number(args.width ?? 5);
        const segments = Number(args.segments ?? 1);
        const outcome = bevelEdges(mesh, componentIds, width, segments);
        replaceObject(next.scene, outcome.mesh);
        next.selection.mode = "face";
        next.selection.componentIds = outcome.selectedIds;
        const params = { width, segments };
        const record = operationRecord(baseSnapshot.scene, request.type, "Bevel Edges", [mesh.id], componentIds, params, outcome.lineage);
        return finalize(baseSnapshot, next, record, outcome.warnings, outcome.lineage);
      }
      case "mesh.insertEdgeLoop": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const edgeId = Number(args.edgeId ?? next.selection.componentIds[0]);
        const position = Number(args.position ?? 0.5);
        const count = Number(args.count ?? 1);
        const outcome = insertEdgeLoop(mesh, edgeId, position, count);
        replaceObject(next.scene, outcome.mesh);
        next.selection.mode = "edge";
        next.selection.componentIds = outcome.selectedIds;
        const params = { edgeId, position, count };
        const record = operationRecord(baseSnapshot.scene, request.type, "Insert Edge Loop", [mesh.id], [edgeId], params, outcome.lineage);
        return finalize(baseSnapshot, next, record, outcome.warnings, outcome.lineage);
      }
      case "uv.projectBox": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const params = {
          scale: Number(args.scale ?? 1),
          rotation: Number(args.rotation ?? 0),
          offsetU: Number(args.offsetU ?? 0),
          offsetV: Number(args.offsetV ?? 0),
        };
        replaceObject(next.scene, projectBoxUV(mesh, params.scale, params.rotation, [params.offsetU, params.offsetV]));
        const record = operationRecord(baseSnapshot.scene, request.type, "Box UV Projection", [mesh.id], [], params, {});
        return finalize(baseSnapshot, next, record);
      }
      case "uv.projectPlanar": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const axis = (args.axis ?? "y") as "x" | "y" | "z";
        const params = {
          axis,
          scale: Number(args.scale ?? 1),
          rotation: Number(args.rotation ?? 0),
          offsetU: Number(args.offsetU ?? 0),
          offsetV: Number(args.offsetV ?? 0),
        };
        replaceObject(next.scene, projectPlanarUV(mesh, axis, params.scale, params.rotation, [params.offsetU, params.offsetV]));
        const record = operationRecord(baseSnapshot.scene, request.type, "Planar UV Projection", [mesh.id], [], params, {});
        return finalize(baseSnapshot, next, record);
      }
      case "material.create": {
        const material: MaterialData = {
          ...structuredClone(DEFAULT_MATERIAL),
          id: String(args.id ?? stableId("material", request.id)),
          name: String(args.name ?? `Material ${next.scene.materials.length + 1}`),
          color: String(args.color ?? "#c8b68f"),
        };
        next.scene.materials.push(material);
        const record = operationRecord(baseSnapshot.scene, request.type, "Create Material", [], [], { materialId: material.id, name: material.name }, {});
        return finalize(baseSnapshot, next, record);
      }
      case "material.update": {
        const material = next.scene.materials.find((candidate) => candidate.id === String(args.materialId));
        if (!material) throw new Error("The selected material no longer exists.");
        if (args.name !== undefined) material.name = String(args.name);
        if (args.color !== undefined) material.color = String(args.color);
        if (args.roughness !== undefined) material.roughness = Number(args.roughness);
        if (args.metallic !== undefined) material.metallic = Number(args.metallic);
        for (const key of ["baseColorTextureId", "normalTextureId", "roughnessTextureId", "metallicTextureId"] as const) {
          if (key in args) material[key] = args[key] ? String(args[key]) : undefined;
        }
        const record = operationRecord(baseSnapshot.scene, request.type, "Edit Material", [], [], structuredClone(args), {});
        return finalize(baseSnapshot, next, record);
      }
      case "material.assign": {
        const mesh = activeMesh(next, args.objectId as string | undefined);
        const materialId = String(args.materialId);
        if (!next.scene.materials.some((material) => material.id === materialId)) throw new Error("The requested material does not exist.");
        const faceIds = new Set((args.faceIds ?? (next.selection.mode === "face" ? next.selection.componentIds : mesh.faces.map((face) => face.id))) as number[]);
        for (const face of mesh.faces) if (faceIds.has(face.id)) face.materialId = materialId;
        const record = operationRecord(baseSnapshot.scene, request.type, "Assign Material", [mesh.id], [...faceIds], { materialId }, {});
        return finalize(baseSnapshot, next, record);
      }
      case "texture.add": {
        const texture: TextureAsset = {
          id: String(args.id ?? stableId("texture", request.id)),
          name: String(args.name ?? "Texture"),
          mimeType: String(args.mimeType ?? "image/png"),
          dataUrl: String(args.dataUrl ?? ""),
        };
        if (!texture.dataUrl.startsWith("data:image/")) throw new Error("Texture data must be an embedded image data URL.");
        next.scene.textures.push(texture);
        const record = operationRecord(baseSnapshot.scene, request.type, "Add Texture", [], [], { textureId: texture.id, name: texture.name }, {});
        const applied = finalize(baseSnapshot, next, record);
        applied.result.lineage.texture = [next.scene.textures.length - 1];
        return applied;
      }
      case "mesh.replaceFromImport": {
        const mesh = refreshEdgeIds(args.mesh as MeshData);
        next.scene.objects.push(mesh);
        next.scene.activeObjectId = mesh.id;
        next.selection = { mode: "object", objectIds: [mesh.id], componentIds: [] };
        const record = operationRecord(baseSnapshot.scene, request.type, "Import Mesh", [mesh.id], [], { name: mesh.name }, {});
        return finalize(baseSnapshot, next, record);
      }
      default: {
        const failed = failure(baseSnapshot.scene, "UNKNOWN_COMMAND", `Unknown command: ${request.type}`);
        failed.snapshot = baseSnapshot;
        return failed;
      }
    }
  } catch (error) {
    const failed = failure(baseSnapshot.scene, "COMMAND_FAILED", error instanceof Error ? error.message : String(error));
    failed.snapshot = baseSnapshot;
    return failed;
  }
}
